import request from '@/utils/request'
import { AxiosPromise } from 'axios'
import {
  WechatJSSignature,
  DictItem,
  House,
  Parking,
  Plate,
  AccountRequestParam,
  AccountResponse,
  OrganizationItem,
  OwnerCreatePayload
} from '@/interfaces/basic'

interface OwnerServiceItem {
  serviceGroup: 'culture' | 'life' | 'special'
  serviceCategory: string
  itemCode: string
  itemName: string
  isOther: boolean
  otherText: string | null
}

interface OwnerRegistrationPerson {
  name: string
  gender: string
  idCardNo: string
  mobile: string
}

interface OwnerRegistrationPayload {
  id?: number | null
  houseId: string
  residentType: string
  houseStatus: string
  deliveryDate: string
  decorationDate: string
  maritalStatus: string
  nation: string
  occupation: string
  companyName: string
  domicileAddress: string
  contactAddress: string
  postalCode: string
  extraServiceNeed: string
  facilityEvalStatus?: number
  facilityEvalMissing: string
  remark: string
  mainPerson: OwnerRegistrationPerson
  coOwnerPerson: OwnerRegistrationPerson | null
  familyMembers: Array<{
    name: string
    relationType: string
    birthDate: string
    contactPhone: string
    healthStatus: string
    sortNo: number
  }>
  vehicles: Array<{
    vehicleType: string
    brandModel: string
    color: string
    plateNo: string
    otherInfo: string
    sortNo: number
  }>
  pets: Array<{
    petType: string
    petBreed: string
    petCount: string
    dogLicenseHandled: string
    vaccinatedRegularly: string
    sortNo: number
  }>
  serviceItems: OwnerServiceItem[]
}

export interface OwnerRegistrationDetail extends OwnerRegistrationPayload {
  id: number
  registerNo?: string
  status?: 'draft' | 'submitted' | 'reviewed'
}

interface OwnerSubmitResponse {
  id: number
  status: string
}

interface SurveyOptionMap {
  label: string
  code: string
}

const surveyOptionMaps = {
  culturalInterests: [
    { label: '阅读', code: 'reading' },
    { label: '写作', code: 'writing' },
    { label: '电影', code: 'movie' },
    { label: '音乐', code: 'music' },
    { label: '舞蹈', code: 'dance' },
    { label: '美术', code: 'art' },
    { label: '书法', code: 'calligraphy' },
    { label: '戏曲', code: 'opera' },
    { label: '棋牌', code: 'chess' },
    { label: '摄影', code: 'photo' },
    { label: '沙龙', code: 'salon' },
    { label: '乐器', code: 'instrument' },
    { label: '手工', code: 'handmade' },
    { label: '旅游', code: 'travel' },
    { label: '其他', code: 'other' }
  ],
  sportsInterests: [
    { label: '乒乓球', code: 'table_tennis' },
    { label: '羽毛球', code: 'badminton' },
    { label: '网球', code: 'tennis' },
    { label: '篮球', code: 'basketball' },
    { label: '排球', code: 'volleyball' },
    { label: '足球', code: 'football' },
    { label: '高尔夫', code: 'golf' },
    { label: '轮滑', code: 'roller' },
    { label: '瑜伽', code: 'yoga' },
    { label: '自行车', code: 'cycling' },
    { label: '武术', code: 'martial_arts' },
    { label: '搏击', code: 'boxing' },
    { label: '跆拳道', code: 'taekwondo' },
    { label: '其他', code: 'other' }
  ],
  educationInterests: [
    { label: '四点半课堂', code: 'after_school_class' },
    { label: '少儿培训班', code: 'children_training' },
    { label: '老年大学', code: 'senior_university' },
    { label: '其他', code: 'other' }
  ],
  diningServices: [
    { label: '电话预约送餐', code: 'meal_delivery' },
    { label: '老年儿童营养配餐', code: 'nutrition_meal' },
    { label: '留守老人或小孩配餐', code: 'meal_care' },
    { label: '其他', code: 'other' }
  ],
  errandServices: [
    { label: '日用品代购', code: 'grocery_purchase' },
    { label: '代订花束', code: 'flower_booking' },
    { label: '房屋代租售', code: 'house_rent_sale' },
    { label: '饮用水代送', code: 'water_delivery' },
    { label: '其他', code: 'other' }
  ],
  specialServices: [
    { label: '家政服务', code: 'housekeeping' },
    { label: '陪诊服务', code: 'accompaniment' },
    { label: '绿植托管', code: 'plant_care' },
    { label: '宠物托管', code: 'pet_care' },
    { label: '其他', code: 'other' }
  ]
} as const

function buildSurveyItems (
  selected: string[],
  options: readonly SurveyOptionMap[],
  serviceGroup: OwnerServiceItem['serviceGroup'],
  serviceCategory: string,
  otherText: string
): OwnerServiceItem[] {
  return selected
    .map(value => {
      const option = options.find(item => item.label === value)
      if (!option) return null
      const isOther = option.code === 'other'
      return {
        serviceGroup,
        serviceCategory,
        itemCode: option.code,
        itemName: option.label,
        isOther,
        otherText: isOther ? (otherText || '') : null
      }
    })
    .filter((item): item is OwnerServiceItem => item !== null)
}

export function buildOwnerRegistrationPayload (data: OwnerCreatePayload): OwnerRegistrationPayload {
  const mainPerson: OwnerRegistrationPerson = {
    name: data.ownerName,
    gender: data.gender,
    idCardNo: data.idCardNo,
    mobile: data.ownerMobile
  }

  const coOwnerPerson = data.coOwnerName || data.coOwnerGender || data.coOwnerIdCardNo || data.coOwnerMobile
    ? {
        name: data.coOwnerName,
        gender: data.coOwnerGender,
        idCardNo: data.coOwnerIdCardNo,
        mobile: data.coOwnerMobile
      }
    : null

  const serviceItems: OwnerServiceItem[] = [
    ...buildSurveyItems(data.survey.culturalInterests, surveyOptionMaps.culturalInterests, 'culture', 'entertainment', data.survey.culturalOther),
    ...buildSurveyItems(data.survey.sportsInterests, surveyOptionMaps.sportsInterests, 'culture', 'sports', data.survey.sportsOther),
    ...buildSurveyItems(data.survey.educationInterests, surveyOptionMaps.educationInterests, 'culture', 'education', data.survey.educationOther),
    ...buildSurveyItems(data.survey.diningServices, surveyOptionMaps.diningServices, 'life', 'dining', data.survey.diningOther),
    ...buildSurveyItems(data.survey.errandServices, surveyOptionMaps.errandServices, 'life', 'errand', data.survey.errandOther),
    ...buildSurveyItems(data.survey.specialServices, surveyOptionMaps.specialServices, 'special', 'special', data.survey.specialOther)
  ]

  return {
    id: data.id ?? null,
    houseId: data.houseUuid || String(data.houseId || ''),
    residentType: data.ownerType,
    houseStatus: data.houseStatus,
    deliveryDate: data.deliveryDate,
    decorationDate: data.decorationDate,
    maritalStatus: data.maritalStatus,
    nation: data.ethnicity,
    occupation: data.occupation,
    companyName: data.company,
    domicileAddress: data.domicileAddress,
    contactAddress: data.contactAddress,
    postalCode: data.postalCode,
    extraServiceNeed: data.survey.extraServiceNeeds,
    facilityEvalStatus: data.survey.facilityEvaluation === 'satisfied'
      ? 1
      : data.survey.facilityEvaluation === 'missing'
        ? 2
        : undefined,
    facilityEvalMissing: data.survey.missingFacilityName,
    remark: data.vehicleRemark,
    mainPerson,
    coOwnerPerson,
    familyMembers: data.familyMembers.map((item, index) => ({
      name: item.name,
      relationType: item.relation,
      birthDate: item.birthDate,
      contactPhone: item.phoneNumber,
      healthStatus: item.healthStatus,
      sortNo: index + 1
    })),
    vehicles: data.vehicles.map((item, index) => ({
      vehicleType: item.vehicleType,
      brandModel: item.brandModel,
      color: item.color,
      plateNo: item.plateNumber,
      otherInfo: item.remark || '',
      sortNo: index + 1
    })),
    pets: data.pets.map((item, index) => ({
      petType: item.petType,
      petBreed: item.petBreed,
      petCount: item.petCount,
      dogLicenseHandled: item.dogLicenseHandled,
      vaccinatedRegularly: item.vaccinatedRegularly,
      sortNo: index + 1
    })),
    serviceItems
  }
}

export function getDictById (id: string): AxiosPromise<Array<DictItem>> {
  return request({ url: `/dicts/${id}`, method: 'get' })
}

export function getAllDict (areaId: number): AxiosPromise<Array<DictItem>> {
  return request({ url: `/mobile/areas/${areaId}/basic/dictionaries`, method: 'get' })
}

export function getHouseById (areaId: number, houseId: number): AxiosPromise<House> {
  return request({ url: `/mobile/areas/${areaId}/basic/houses/${houseId}`, method: 'get' })
}

export function getParkingById (areaId: number, parkingId: number): AxiosPromise<Parking> {
  return request({ url: `/mobile/areas/${areaId}/basic/parking/${parkingId}`, method: 'get' })
}

export function getPlateById (areaId: number, plateId: number): AxiosPromise<Plate> {
  return request({ url: `/mobile/areas/${areaId}/basic/plates/${plateId}`, method: 'get' })
}

export function getWechatJSSignature (url: string): AxiosPromise<WechatJSSignature> {
  return request({
    url: '/mobile/account/wechat/getSignature',
    method: 'get',
    params: { signUrl: url }
  })
}

export function getAccounts (areaId: number, params: AccountRequestParam): AxiosPromise<AccountResponse> {
  return request({ url: `/mobile/areas/${areaId}/basic/accounts`, method: 'get', params })
}

export function getOrganization (areaId: number): AxiosPromise<OrganizationItem[]> {
  return request({ url: `/mobile/areas/${areaId}/basic/operate-organization/sub`, method: 'get' })
}

export function getOwnerRegistrationByHouse (areaId: number, houseId: number): AxiosPromise<OwnerRegistrationDetail | null> {
  return request({
    url: `/mobile/areas/${areaId}/owner-registration/houses/${houseId}`,
    method: 'get'
  })
}

export function getOwnerRegistrationDetail (areaId: number, id: number): AxiosPromise<OwnerRegistrationDetail> {
  return request({
    url: `/mobile/areas/${areaId}/owner-registration/${id}`,
    method: 'get'
  })
}

export function saveOwnerRegistrationDraft (areaId: number, data: OwnerCreatePayload): AxiosPromise<number> {
  return request({
    url: `/mobile/areas/${areaId}/owner-registration/draft`,
    method: 'post',
    data: buildOwnerRegistrationPayload(data)
  })
}

export function saveOwnerRegistrationDirect (areaId: number, data: OwnerCreatePayload): AxiosPromise<number> {
  return request({
    url: `/mobile/areas/${areaId}/owner-registration/save`,
    method: 'post',
    data: buildOwnerRegistrationPayload(data)
  })
}

export function submitOwnerRegistration (areaId: number, id: number): AxiosPromise<OwnerSubmitResponse> {
  return request({
    url: `/mobile/areas/${areaId}/owner-registration/${id}/submit`,
    method: 'put'
  })
}
